<template>
  <v-expansion-panel :key="item.id">
    <v-expansion-panel-header :class="{ 'red lighten-3': item.active === false }">
      <h3>
        {{ item.labelOrganisation }}
      </h3>

      <div class="d-flex justify-end" style="margin-right: 16px">
        <v-tooltip bottom v-if="!isNew">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click="emitEdition(item)" class="ma-2 white--text" color="primary">
              <v-icon left>mdi-pencil</v-icon>
              Éditer
            </v-btn>
          </template>
          <span>Modifier la carte d'affaire</span>
        </v-tooltip>

        <v-tooltip bottom v-if="item.active === true">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" class="ma-2 white--text" @click="openDialog(item)" color="#8b0000">
              <v-icon left>mdi-delete</v-icon>
              Supprimer
            </v-btn>
          </template>
          <span>Supprimer la carte d'affaire</span>
        </v-tooltip>

        <v-tooltip bottom v-if="item.active === false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon @click="reactiverCarteDAffaire" color="primary">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Réactiver la carte d'affaire</span>
        </v-tooltip>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content eager>
      <v-sheet name="item.id">
        <Organisation
          :key="item.id"
          :carteDAffaire="item"
          :indexCarteDAffaire="item.internalIndex"
          :listeProvinces="listeProvinces"
          :listePays="listePays"
          :isDroitsAcquis="isDroitsAcquis"
          :disabled="!(isNew && item.cartePrecedente === undefined)"
          :rules="rules"
          @validate-form="validateForm"
          @organisationChange="organisationChange"
        ></Organisation>

        <fieldset v-if="!isSansPrecisions">
          <legend class="text-h6 text-sm-h5">Lieu(x) de travail</legend>
          <div>
            <v-checkbox
              color="primary"
              v-model="item.adresseIdentiqueALEmployeur"
              label="Mon lieu de travail est à la même adresse que mon employeur"
              hide-details
              @change="copieAdresseEmployeur($event)"
              :disabled="!canBeIdentiqueEmployeur || !isNew"
            >
            </v-checkbox>

            <!-- Employer details -->
            <div v-if="afficherAdresseLongue" class="ml-16">
              <!-- Nom du lieu de travail -->
              <v-text-field
                label="Nom du lieu de travail"
                v-model="item.nomDuLieuDeTravail"
                :rules="rules.nomDuLieuDeTravail"
                :disabled="!isNew || item.adresseIdentiqueALEmployeur"
                required
                class="cssRequired"
              ></v-text-field>

              <!-- Adresse -->
              <CanadaPost
                @updateAdresse="updateLieuxDeTravailAddress"
                :clef="getKey(item.internalIndex)"
                text="Adresse du lieu de travail"
                v-model="item.rue1"
                :disabled="!isNew || item.adresseIdentiqueALEmployeur"
                required
                cssClass="cssRequired"
              />

              <!-- Ville -->
              <v-text-field label="Ville" v-model="item.ville" disabled required class="cssRequired"></v-text-field>

              <!-- Code Postal -->
              <v-text-field label="Code postal" v-model="item.codePostal" disabled required class="cssRequired"></v-text-field>

              <!-- Province -->
              <v-autocomplete
                :items="listeProvinces"
                :item-text="itemText"
                :item-value="itemValue"
                v-model="item.province"
                label="Province"
                return-object
                disabled
                required
                class="cssRequired"
              ></v-autocomplete>

              <!-- Pays -->
              <v-autocomplete
                :items="listePays"
                :item-text="itemText"
                :item-value="itemValue"
                v-model="item.pays"
                label="Pays"
                return-object
                disabled
                required
                class="cssRequired"
              ></v-autocomplete>
            </div>
            <div v-else>
              <address class="ml-8" style="color: rgba(0, 0, 0, 0.38)">
                <div>{{ item.nomDuLieuDeTravail }}</div>
                <div>{{ item.rue1 }}</div>
                <div>
                  <span v-if="item.ville">{{ item.ville }}, </span>
                  <span v-if="item.province">{{ item.province.lang00 }}, </span>
                  <span v-if="item.codePostal">{{ item.codePostal }}, </span>
                  <span v-if="item.pays">{{ item.pays.lang00 }}</span>
                </div>
              </address>
            </div>

            <!-- Tel -->
            <v-phone-input v-model="item.tel" label="Téléphone" :disabled="!isNew"></v-phone-input>

            <!-- Poste -->
            <v-text-field label="Poste" v-model="item.telDirectPoste" :disabled="!isNew"></v-text-field>

            <!-- Cellulaire -->
            <v-phone-input v-model="item.cellulaire" label="Cellulaire" :disabled="!isNew"></v-phone-input>

            <!-- Courriel électronique professionnel -->
            <v-text-field label="Courriel électronique professionnel" v-model="item.courriel" :disabled="!isNew"></v-text-field>

            <!-- Fonction / Titre d'emploi -->
            <v-autocomplete
              :items="listeFonctions"
              :item-text="itemText"
              :item-value="itemValue"
              v-model="item.fonction"
              label="Fonction"
              :disabled="!isNew"
              return-object
            ></v-autocomplete>

            <!-- Fonction détaillée -->
            <v-text-field label="Titre d'emploi" v-model="item.titreDEmploi" :disabled="!isNew"></v-text-field>

            <!-- Date d'entrée en fonction -->
            <v-menu
              ref="menu"
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
              autocomplete="off"
              :disabled="!isNew"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-sheet>
                  <!-- Date d'entrée en fonction -->
                  <v-text-field
                    v-model="item.dateDEntreeEnFonction"
                    label="Date d'entrée en fonction"
                    append-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    v-mask="'####-##-##'"
                    hint="Format YYYY-MM-DD"
                    readonly
                    :disabled="!isNew"
                    required
                    :rules="rules.dateDEntreeEnFonction"
                    class="cssRequired"
                  />
                </v-sheet>
              </template>
              <v-date-picker
                ref="picker"
                v-model="item.dateDEntreeEnFonction"
                :disabled="!isNew"
                required
                :rules="rules.dateDEntreeEnFonction"
                @change="save"
              />
            </v-menu>

            <!-- Domicile professionnel -->
            <v-checkbox
              color="primary"
              v-model="item.principalPro"
              label="Ceci est mon lieu de travail principal"
              hide-details
              :disabled="!isNew"
              @change="emitAdressePrincipaleUnique"
            >
            </v-checkbox>

            <section class="legalSection body-1">
              <p class="mentionLegal">
                Votre lieu de travail principal est celui où vous travaillez la majeure partie du temps, que ce soit dans le cadre d'un emploi salarié
                ou d'une pratique autonome. Le lieu de travail est le nom de l'installation (parfois appelé « site ») où vous exercez tous les jours
                votre emploi (exemple : CLSC de Saint-Louis-du-Parc).
              </p>
              <p class="font-italic">Les coordonnées de votre lieu de travail peuvent être identiques ou différentes de celles de votre employeur.</p>
            </section>
          </div>
          <div v-if="!isDroitsAcquis && !isSansPrecisions && !isCategorieAutorisationPrp">
            <h5 class="text-h6 text-sm-h5">Pratique autonome</h5>
            <section class="legalSection">
              <p class="mentionLegal">
                Selon l'article 60 du Code des professions, vous avez l'obligation légale de remplir cette section, si vous exercez en pratique
                autonome.
              </p>
            </section>

            <!-- Pratique autonome -->
            <v-checkbox
              label="À ce lieu de travail, exercez-vous en pratique autonome ?"
              v-model="item.pratiqueAutonome"
              :disabled="!isNew"
              @change="viderPratiqueAutonome(item)"
            ></v-checkbox>

            <!-- Detail de pratique -->
            <div class="ml-16" v-if="item.pratiqueAutonome == true">
              <v-autocomplete
                :items="listeNbHeurePratiqueAutonomne"
                :item-text="itemText"
                :item-value="itemValue"
                v-model="item.nombreDHeureDeTravailEnPratiqueAutonome"
                label="Combien d'heure exercez-vous par semaine? "
                clearable
                return-object
                :rules="rules.nombreDHeureDeTravailEnPratiqueAutonome"
                :disabled="!isNew"
                class="cssRequired"
              ></v-autocomplete>

              <v-autocomplete
                :items="listeInstallationHandicape"
                :item-text="itemText"
                :item-value="itemValue"
                v-model="item.installationsAdapteesAuxPersonnesHandicapees"
                label="Votre bureau possède-t-il des installations adaptées aux personnes handicapées?"
                clearable
                return-object
                :rules="rules.installationsAdapteesAuxPersonnesHandicapees"
                :disabled="!isNew"
                class="cssRequired"
              ></v-autocomplete>

              <p class="mentionLegal">
                Acceptez-vous que l'Ordre affiche sur le service de référence en ligne "Trouver un professionnel" vos coordonnées (nom, prénom, genre,
                langue, autorisation de pratique, lieu de travail principal, expertises, domaine de pratique, type de clientèles desservies, etc)
                Prenez note que si vous pratiquez en pratique autonome à domicile, votre adresse résidentielle devient votre « domicile professionnel
                » selon le Code des professions. Évitez de saisir un casier postal dans l'adresse du lieu de travail pour une meilleure expérience
                client sur le service de référence.
              </p>
              <v-autocomplete
                v-model="item.serviceDeReferenceEnLigne"
                :items="listeServiceDeReferenceEnLigne"
                :item-text="itemText"
                :item-value="itemValue"
                label="Réponse"
                return-object
                :rules="rules.serviceDeReferenceEnLigne"
                :disabled="!isNew"
                :required="isNew"
                class="cssRequired"
              ></v-autocomplete>

              <!-- Ne pas diffuser mon adresse -->
              <div v-if="conditionNePasDiffuserMonAdresse">
                <p class="mentionLegal">
                  Dans le cas d'une pratique autonome, l'Ordre vous permet de ne pas afficher votre numéro et nom de rue lors d'une recherche de
                  professionnel sur notre site. Cocher ici pour activer cette option.
                </p>
                <v-checkbox label="Ne pas diffuser mon adresse" v-model="item.nePasDiffuserMonAdresse" :disabled="!isNew"></v-checkbox>
              </div>
            </div>
          </div>
          <div v-if="!isDroitsAcquis && !isSansPrecisions">
            <h5 class="text-h6 text-sm-h5">Informations de travail</h5>
            <section class="legalSection">
              <p class="mentionLegal">
                Veuillez réviser les renseignements professionnels qui se trouvent ci-dessous. Si vous travaillez dans un autre domaine, que vous êtes
                hors Québec, retraité, sans emploi ou étudiant, aucune information est requise.
              </p>
            </section>
            <v-expansion-panels inset>
              <!-- Domaines de pratique -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeDomainesDePratique"
                label="Domaines de pratique"
                v-model="item.domainesDePratique"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventDomainesDePratique"
              >
              </TreeviewCatalogue>

              <!-- Problèmes sociaux généraux -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeProblemesSociauxGeneraux"
                label="Problèmes généraux"
                v-model="item.problemesSociauxGeneraux"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventProblemesSociauxGeneraux"
              >
              </TreeviewCatalogue>

              <!-- Services offerts-->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeServicesOfferts"
                label="Services offerts"
                v-model="item.servicesOfferts"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventServicesOfferts"
              >
              </TreeviewCatalogue>

              <!-- Types de clientèle desservie -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeTypeClientele"
                label="Types de clientèle desservie"
                v-model="item.typesDeClienteleDesservie"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventTypesDeClientelesDesservie"
              >
              </TreeviewCatalogue>

              <!-- Méthodes d'intervention -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeMethodesInterventions"
                label="Méthodes d'intervention"
                v-model="item.methodesDIntervention"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventMethodesDIntervention"
              >
              </TreeviewCatalogue>

              <!-- Approches d'intervention -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeApprochesInterventions"
                label="Approches d'intervention"
                v-model="item.approchesDIntervention"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventApprochesDIntervention"
              >
              </TreeviewCatalogue>

              <!-- Thérapie/techniques d'intervention -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeTherapieTechniquesDIntervention"
                label="Thérapies / Techniques d'intervention"
                v-model="item.therapieTechniquesDIntervention"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventTherapieTechniquesDIntervention"
              >
              </TreeviewCatalogue>

              <!-- Expertises légales/Activitées réservées -->
              <TreeviewCatalogue
                :index="item.internalIndex"
                :items="listeExpertisesLegalesActivitesReservees"
                label="Expertises légales / Activités réservées"
                v-model="item.expertiseLegalesActivitesReservees"
                multiple
                :disabled="!isNew"
                @updateCatalogue="eventExpertiseLegalesActivitesReservees"
              >
              </TreeviewCatalogue>
            </v-expansion-panels>
          </div>
        </fieldset>
      </v-sheet>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"
import Organisation from "./Organisation"
import CanadaPost from "./canadaPost/CanadaPost"
import TreeviewCatalogue from "./TreeviewCatalogue"
import { isDroitsAcquis, isCategorieAutorisationPrp } from "@/utils/autorisationHelper"
import { pratiqueAutonomeId } from "@/plugins/constants"

export default {
  props: {
    item: {
      type: Object,
      default: null
    },
    valid: {
      type: Boolean,
      default: true
    }
  },
  components: {
    Organisation,
    CanadaPost: CanadaPost,
    TreeviewCatalogue,
  },
  data() {
    return {
      rules: {
        nombreDHeureDeTravailEnPratiqueAutonome: [(v) => (!!v && v.length != 0) || "Le nombre d'heure de travail en pratique autonome est requis"],
        installationsAdapteesAuxPersonnesHandicapees: [
          (v) => (!!v && v.length != 0) || "La question des installations adaptées aux personnes handicapées est requise"
        ],
        serviceDeReferenceEnLigne: [(v) => (!!v && v.length != 0) || "Le service de référence en ligne est requis"],
        adresse: [(v) => !!v || "L'adresse est requise"],
        ville: [(v) => !!v || "La ville est requise"],
        provinceOuEtatSiApplicable: [(v) => (!!v && v.length != 0) || "La province ou l'état est requis"],
        pays: [(v) => (!!v && v.length != 0) || "Le pays est requis"],
        postalCode: [(v) => !!v || "Le code postal est requis"],
        nomDuLieuDeTravail: [(v) => !!v || "Le nom du lieu de travail est requis"],
        tel: [(v) => !!v || "Le numéro de téléphone est requis"],
        poste: [(v) => !!v || "Le numéro de poste est requis"],
        courriel: [(v) => !!v || "Le courriel est requis"],
        titreDEmploi: [(v) => !!v || "Le titre d'emploi est requis"],
        dateDEntreeEnFonction: [(v) => !!v || "La date d'entrée en fonction est requise"]
      },
      dateMenu: false,
      isOrganisationSansPrecisions: false
    }
  },
  computed: {
    ...mapGetters({
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      demandeInscriptionPpId: "DemandeInscription/GET_CONTACTID",
      cartesDAffaires: "CarteDAffaire/getCartesDAffaires",
      listeNbHeurePratiqueAutonomne: "Catalogue/GET_NBHEUREPRATIQUEAUTONOMNE",
      listeInstallationHandicape: "Catalogue/GET_INSTALATIONHANDICAPE",
      listeFonctions: "Catalogue/GET_FONCTIONS",
      listeDomainesDePratique: "Catalogue/GET_DOMAINESDEPRATIQUE",
      listeTypeClientele: "Catalogue/GET_TYPECLIENT",
      listeMethodesInterventions: "Catalogue/GET_METHODESDINTERVENTION",
      listeApprochesInterventions: "Catalogue/GET_APPROCHESDINTERVENTION",
      listeServicesOfferts: "Catalogue/GET_SERVICESOFFERTS",
      listeProblemesSociauxGeneraux: "Catalogue/getProblemesSociauxGeneraux",
      listeTherapieTechniquesDIntervention: "Catalogue/getTherapieTechniquesDIntervention",
      listeExpertisesLegalesActivitesReservees: "Catalogue/getExpertiseLegalesActivitesReservees",
      listePays: "Catalogue/GET_PAYS",
      listeProvinces: "Catalogue/GET_PROVINCES",
      listeServiceDeReferenceEnLigne: "Catalogue/getServicesDeReferenceEnLigne",
      getAutorisations: "AutorisationDePratique/GET_AUTORISATIONSDEPRATIQUE"
    }),
    isDroitsAcquis() {
      return isDroitsAcquis(this.getAutorisations)
    },
    isCategorieAutorisationPrp() {
      return isCategorieAutorisationPrp(this.getAutorisations)
    },
    isSansPrecisions() {
      return this.isOrganisationSansPrecisions
    },
    isPratiqueAutonome() {
      return this.item.organisation?.id === pratiqueAutonomeId
    },
    conditionNePasDiffuserMonAdresse() {
      return this.isPratiqueAutonome && this.item.serviceDeReferenceEnLigne?.id === this.$catalogues.serviceDeReferenceEnLigne.oui
    },
    isNew() {
      return this.item.id === null
    },
    afficherAdresseLongue() {
      return (this.item.id == null && !this.item.adresseIdentiqueALEmployeur) || !this.item.rue1
    },
    // indique si l'adresse du lieu de travail peut être recopiée depuis l"employeur
    canBeIdentiqueEmployeur() {
      const employeurValid = this.item.organisation && this.item.organisation.rue1 !== undefined && this.item.organisation.rue1 !== null

      const result = employeurValid === false || this.isPratiqueAutonome

      if (result) {
        this.item.adresseIdentiqueALEmployeur = false
      }

      return !result
    }
  },
  methods: {
    ...mapMutations({
      updateDomainesDePratique: "CarteDAffaire/updateDomainesDePratique",
      updateApprochesDIntervention: "CarteDAffaire/updateApprochesDIntervention",
      updateMethodesDIntervention: "CarteDAffaire/updateMethodesDIntervention",
      updateServicesOfferts: "CarteDAffaire/updateServicesOfferts",
      updateTypesDeClientelesDesservie: "CarteDAffaire/updateTypesDeClientelesDesservie",
      updateProblemesSociauxGeneraux: "CarteDAffaire/updateProblemesSociauxGeneraux",
      updateTherapieTechniquesDIntervention: "CarteDAffaire/updateTherapieTechniquesDIntervention",
      updateExpertiseLegalesActivitesReservees: "CarteDAffaire/updateExpertiseLegalesActivitesReservees"
    }),
    ...mapActions({
      setAdresseOrganisationToCarteDAffaire: "CarteDAffaire/setAdresseOrganisationToCarteDAffaire",
      updateFromCanadaPost: "CarteDAffaire/updateFromCanadaPost"
    }),
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    copieAdresseEmployeur(event) {
      if (event) {
        this.setAdresseOrganisationToCarteDAffaire(this.item)
      }
    },
    getKey(index) {
      return "l-" + index
    },
    updateLieuxDeTravailAddress(payload) {
      this.updateFromCanadaPost({
        carteDAffaire: this.item,
        address: payload,
        listePays: this.listePays,
        listeProvinces: this.listeProvinces
      })
    },
    eventDomainesDePratique(payload) {
      this.updateDomainesDePratique({ value: payload.value, carteDAffaire: this.item })
    },
    eventApprochesDIntervention(payload) {
      this.updateApprochesDIntervention({ value: payload.value, carteDAffaire: this.item })
    },
    eventMethodesDIntervention(payload) {
      this.updateMethodesDIntervention({ value: payload.value, carteDAffaire: this.item })
    },
    eventServicesOfferts(payload) {
      this.updateServicesOfferts({ value: payload.value, carteDAffaire: this.item })
    },
    eventTypesDeClientelesDesservie(payload) {
      this.updateTypesDeClientelesDesservie({ value: payload.value, carteDAffaire: this.item })
    },
    eventProblemesSociauxGeneraux(payload) {
      this.updateProblemesSociauxGeneraux({ value: payload.value, carteDAffaire: this.item })
    },
    eventTherapieTechniquesDIntervention(payload) {
      this.updateTherapieTechniquesDIntervention({ value: payload.value, carteDAffaire: this.item })
    },
    eventExpertiseLegalesActivitesReservees(payload) {
      this.updateExpertiseLegalesActivitesReservees({ value: payload.value, carteDAffaire: this.item })
    },
    viderPratiqueAutonome(item) {
      item.nombreDHeureDeTravailEnPratiqueAutonome = null
      item.installationsAdapteesAuxPersonnesHandicapees = null
      item.serviceDeReferenceEnLigne = null
      item.nePasDiffuserMonAdresse = false
    },
    validateForm() {
      this.$emit("validate-form")
    },
    openDialog(item) {
      this.$emit("openDialog", item)
    },
    organisationChange(value) {
      this.isOrganisationSansPrecisions = value

      this.$nextTick(() => {
        if (this.isPratiqueAutonome) {
          this.item.pratiqueAutonome = true
        } else {
          this.item.nePasDiffuserMonAdresse = false
        }
      })
    },
    reactiverCarteDAffaire() {
      this.$set(this.item, "active", true)
      this.validateForm()
    },
    emitAdressePrincipaleUnique() {
      this.$emit("adressePrincipaleUnique", this.item)
    },
    emitEdition(item) {
      this.$emit("edition", item)
    },
    save(date) {
      this.$refs.menu.save(date)
    },
    telValideSiRempli(tel) {
      if (tel != "" && tel != null) {
        return this.validatePhone(tel)
      }
      return true
    }
  }
}
</script>